/**
 * Guarded access to Web Storage (`localStorage`/`sessionStorage`).
 *
 * Some browsers, extensions, and private-mode contexts block storage — accessing the `localStorage`
 * getter itself can throw, and even a resolved store can throw on read/write (blocked, or over
 * quota). {@link SafeLocalStorage} wraps a store so every operation degrades to a graceful no-op /
 * null / false / empty list instead of crashing the caller. Construct it once (default is
 * `localStorage`, resolved defensively; inject a store in tests) and use it like `localStorage`.
 * Also SSR-safe: `window` being undefined resolves to no store.
 *
 * Lives here rather than in one app because the crash it prevents is worst in shared components —
 * a throw from a render-phase read in a provider that wraps the whole tree takes down the app.
 */
/** Resolves the store to use, or null when it's unavailable. */
export var resolveLocalStorage = function (storage) {
    if (storage !== undefined) {
        return storage;
    }
    try {
        return window.localStorage;
    }
    catch (_a) {
        return null;
    }
};
var SafeLocalStorage = /** @class */ (function () {
    /** @param store - Underlying store; defaults to a defensively-resolved `localStorage`. */
    function SafeLocalStorage(store) {
        this.store = resolveLocalStorage(store);
    }
    Object.defineProperty(SafeLocalStorage.prototype, "available", {
        /** Whether a usable underlying store was resolved. */
        get: function () {
            return this.store !== null;
        },
        enumerable: false,
        configurable: true
    });
    /** Reads a key, or null when storage is unavailable or the read throws. */
    SafeLocalStorage.prototype.getItem = function (key) {
        if (this.store === null) {
            return null;
        }
        try {
            return this.store.getItem(key);
        }
        catch (_a) {
            return null;
        }
    };
    /** Writes a key; returns whether it succeeded (false when unavailable, blocked, or over quota). */
    SafeLocalStorage.prototype.setItem = function (key, value) {
        if (this.store === null) {
            return false;
        }
        try {
            this.store.setItem(key, value);
            return true;
        }
        catch (_a) {
            return false;
        }
    };
    /** Removes a key; a no-op when storage is unavailable or the removal throws. */
    SafeLocalStorage.prototype.removeItem = function (key) {
        if (this.store === null) {
            return;
        }
        try {
            this.store.removeItem(key);
        }
        catch (_a) {
            // Blocked — nothing to clean up.
        }
    };
    /** Snapshot of all keys, or an empty list when storage is unavailable or enumeration throws. */
    SafeLocalStorage.prototype.keys = function () {
        if (this.store === null) {
            return [];
        }
        try {
            var keys = [];
            for (var index = 0; index < this.store.length; index += 1) {
                var key = this.store.key(index);
                if (key !== null) {
                    keys.push(key);
                }
            }
            return keys;
        }
        catch (_a) {
            return [];
        }
    };
    return SafeLocalStorage;
}());
export { SafeLocalStorage };
